// Currency formatter for INR with grouped thousands (Indian numbering).
export function formatINR(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function defaultInvoiceNumber() {
  const d = new Date();
  const ymd =
    d.getFullYear().toString().slice(-2) +
    String(d.getMonth() + 1).padStart(2, "0") +
    String(d.getDate()).padStart(2, "0");
  const seq = Math.floor(Math.random() * 900 + 100);
  return `INV-${ymd}-${seq}`;
}

export function emptyLineItem() {
  return {
    id: crypto.randomUUID(),
    hsn: "",
    name: "",
    description: "",
    qty: 1,
    unit: "PCS",
    rate: 0,
  };
}

export function calcTotals(items, taxRate) {
  const subtotal = items.reduce(
    (sum, it) => sum + Number(it.qty || 0) * Number(it.rate || 0),
    0,
  );
  const tax = subtotal * (Number(taxRate || 0) / 100);
  const total = subtotal + tax;
  return { subtotal, tax, total };
}

// Convert number to Indian English words (rupees + paise).
const ones = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const tens = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

function twoDigits(n) {
  if (n < 20) return ones[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return tens[t] + (o ? " " + ones[o] : "");
}

function threeDigits(n) {
  const h = Math.floor(n / 100);
  const r = n % 100;
  let s = "";
  if (h) s += ones[h] + " Hundred";
  if (r) s += (h ? " " : "") + twoDigits(r);
  return s;
}

export function amountInWords(amount) {
  const num = Math.floor(Number(amount || 0));
  const paise = Math.round((Number(amount || 0) - num) * 100);
  if (num === 0 && paise === 0) return "Zero Rupees Only";

  let n = num;
  let words = "";

  const crore = Math.floor(n / 10000000);
  n = n % 10000000;
  const lakh = Math.floor(n / 100000);
  n = n % 100000;
  const thousand = Math.floor(n / 1000);
  n = n % 1000;
  const hundred = n;

  if (crore) words += threeDigits(crore) + " Crore ";
  if (lakh) words += twoDigits(lakh) + " Lakh ";
  if (thousand) words += twoDigits(thousand) + " Thousand ";
  if (hundred) words += threeDigits(hundred) + " ";

  let result = "Rupees " + words.trim();
  if (paise > 0) result += " and " + twoDigits(paise) + " Paise";
  return result + " Only";
}

// Determine GST split structure based on company GSTIN vs customer GSTIN.
// Indian GST rule: first 2 digits of GSTIN = state code.
// Same state -> intra-state -> CGST + SGST (each = rate/2).
// Different state OR missing customer GSTIN -> inter-state -> IGST.
export function gstSplit({ subtotal, taxRate, companyGstin, customerGstin }) {
  const rate = Number(taxRate || 0);
  const taxAmount = Number(subtotal || 0) * (rate / 100);

  const companyState = (companyGstin || "").trim().slice(0, 2);
  const customerState = (customerGstin || "").trim().slice(0, 2);

  const intraState =
    companyState.length === 2 &&
    customerState.length === 2 &&
    companyState === customerState;

  if (intraState) {
    const halfRate = rate / 2;
    const halfAmount = taxAmount / 2;
    return {
      mode: "intra",
      cgst: { rate: halfRate, amount: halfAmount },
      sgst: { rate: halfRate, amount: halfAmount },
      igst: null,
      total: taxAmount,
    };
  }

  return {
    mode: "inter",
    cgst: null,
    sgst: null,
    igst: { rate, amount: taxAmount },
    total: taxAmount,
  };
}
